// "What kind of boat is this?" — Power / Sail, relayed to the boat (ADR 0173).
//
// One control, two homes: the per-boat Settings page (where an owner goes to
// change it) and the Alarms page's prompt card (where an owner who has never
// been asked is asked once). Sharing the component is the point — the two must
// send the same verb, show the same words, and be honest in the same way.
//
// NO OPTIMISTIC UI. The selected state is read from the MIRROR (`vesselType`,
// which is the boat's own echo), never from the click. Between the click and
// the echo the control says "Sent to your boat…" and the buttons stay where
// they were: the boat is the one that decides, and a control that painted the
// new answer instantly would be claiming an outcome it does not have. That
// gap is normally under a second; on a marginal link it is honest for longer.
//
// The "in flight" claim is OWNED HERE, as request state, and it is ended by the
// echo or by a deadline — never inferred from "the mirror disagrees with what I
// sent". Somebody answering at the helm, or on the boat's own settings sheet,
// moves the mirror underneath a page nobody has touched and reproduces that
// mismatch exactly; the page would start announcing a command it never sent.
//
// The offline reason is TEXT under the buttons, not a `title`. Same machinery
// as the pause banner next door, deliberately — the two controls sit on the
// same page and must fail the same way.

import { useEffect, useState } from 'react';

import { BOAT_ECHO_TIMEOUT_MS } from '../../lib/boatEcho';
import {
  VESSEL_TYPE_OFFLINE_REASON,
  VESSEL_TYPE_OPTIONS,
  sendVesselType,
  settleVesselType,
  timeOutVesselType,
  vesselTypeStatusText,
  type VesselType,
  type VesselTypeSaveState,
} from '../../lib/vesselType';

interface VesselTypeChoiceProps {
  boatId: string;
  /** The MIRROR — the boat's own answer, `null` when it has never been asked. */
  vesselType: VesselType | null | undefined;
  /**
   * Presence. An offline boat still renders the control (so the owner can see
   * what it says) but cannot be told anything, so the buttons are disabled with
   * a plain reason rather than left live to 409.
   */
  boatOnline: boolean;
  /**
   * Re-read the surface carrying the mirror after a delivered relay. The echo
   * usually arrives on the alerts lane by itself; this covers the case where
   * the page is not subscribed at that instant.
   */
  onSent?: () => void;
  /** Test id prefix — the two homes need distinguishable buttons. */
  testIdPrefix?: string;
}

export function VesselTypeChoice({
  boatId,
  vesselType,
  boatOnline,
  onSent,
  testIdPrefix = 'vessel-type',
}: VesselTypeChoiceProps) {
  const [save, setSave] = useState<VesselTypeSaveState>({ kind: 'idle' });
  const busy = save.kind === 'sending';
  const status = vesselTypeStatusText(save);

  // The boat's echo ends the in-flight request — the ONLY success signal this
  // control accepts. `settleVesselType` returns the same object when nothing
  // changed, so this cannot loop.
  useEffect(() => {
    setSave((prev) => settleVesselType(prev, vesselType));
  }, [vesselType]);

  // …and a deadline ends it when no echo ever comes, so the control stops
  // claiming a command is moving and says what is actually true instead. An
  // older core warn-drops `vessel_type_set` with no nack, which is precisely
  // the case that would otherwise wait forever.
  useEffect(() => {
    if (save.kind !== 'sent') return;
    const id = window.setTimeout(
      () => setSave((prev) => timeOutVesselType(prev)),
      BOAT_ECHO_TIMEOUT_MS
    );
    return () => window.clearTimeout(id);
  }, [save]);

  async function choose(next: VesselType): Promise<void> {
    if (busy) return;
    setSave({ kind: 'sending', vesselType: next });
    const outcome = await sendVesselType(boatId, next);
    setSave(outcome);
    if (outcome.kind === 'sent') onSent?.();
  }

  return (
    <div className="vessel-type" data-testid={testIdPrefix}>
      <div className="vessel-type__options" role="group">
        {VESSEL_TYPE_OPTIONS.map((option) => {
          const selected = vesselType === option.value;
          return (
            <button
              key={option.value}
              type="button"
              className={`vessel-type__option${selected ? ' vessel-type__option--on' : ''}`}
              // The mirror is the truth, so this is what a screen reader hears
              // too — pressed means "your boat says so", not "you clicked it".
              aria-pressed={selected}
              disabled={busy || !boatOnline}
              data-testid={`${testIdPrefix}-${option.value}`}
              onClick={() => void choose(option.value)}
            >
              <span className="vessel-type__label">{option.label}</span>
              <span className="vessel-type__hint">{option.hint}</span>
            </button>
          );
        })}
      </div>
      {/* Rendered as TEXT, under the disabled buttons — never a `title`. See the
          constant's comment: a tooltip does not exist on the phone most of
          these visits come from, so the reason would reach nobody. */}
      {!boatOnline ? (
        <p className="vessel-type__offline" data-testid={`${testIdPrefix}-offline-reason`}>
          {VESSEL_TYPE_OFFLINE_REASON}
        </p>
      ) : null}
      {status !== null ? (
        <p
          className={`vessel-type__status${statusIsWarn(save) ? ' vessel-type__status--warn' : ''}`}
          role="status"
          aria-live="polite"
          data-testid={`${testIdPrefix}-status`}
        >
          {status}
        </p>
      ) : null}
    </div>
  );
}

/** The states whose line is bad news rather than progress. */
function statusIsWarn(save: VesselTypeSaveState): boolean {
  return save.kind === 'error' || save.kind === 'offline' || save.kind === 'unconfirmed';
}
